import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QuickFind as DimQuickFind, type QuickFindGroup } from '@bn-apps/dim-ui-react';
import { quickFindPlaces } from '@config/quickFind';
import { collectText, searchQuickFind, type QuickFindEntry } from '@utils/quickFind.utils';

interface QuickFindProps {
  /** Pages behind sign in are only offered to signed-in users */
  isAuthenticated: boolean;
  /** Admin tools are only offered to project members */
  isProjectMember: boolean;
}

/** The header's Find button: goes to a page, tool or help article by typing part of its name or content. */
const QuickFind = ({ isAuthenticated, isProjectMember }: QuickFindProps): JSX.Element => {
  const { t } = useTranslation();

  const entries = useMemo<QuickFindEntry[]>(
    () =>
      quickFindPlaces({ isAuthenticated, isProjectMember }).map((place) => ({
        kind: place.kind,
        path: place.path,
        title: t(place.labelKey),
        context: place.contextKey && t(place.contextKey),
        description: t(place.descriptionKey),
        // Also found by its English name, its keywords and the text of a help article
        body: [
          t(place.labelKey, { lng: 'en' }),
          place.keywordsKey ? t(place.keywordsKey) : '',
          place.contentKey ? collectText(t(place.contentKey, { returnObjects: true })) : '',
        ].join(' '),
      })),
    [t, isAuthenticated, isProjectMember],
  );

  const search = useCallback(
    (query: string): QuickFindGroup[] => {
      const results = searchQuickFind(entries, query);
      const group = (kind: QuickFindEntry['kind'], label: string): QuickFindGroup => ({
        label,
        items: results
          .filter((entry) => entry.kind === kind)
          .map(({ path, title, context, description }) => ({ id: path, href: path, title, context, description })),
      });
      return [group('page', t('quickFind.pages')), group('help', t('help.title'))];
    },
    [entries, t],
  );

  return (
    <DimQuickFind
      search={search}
      labels={{
        button: t('quickFind.button'),
        title: t('quickFind.title'),
        placeholder: t('quickFind.placeholder'),
        noResults: (query) => t('quickFind.noResults', { query: query.trim() }),
        move: t('quickFind.move'),
        open: t('quickFind.open'),
        close: t('quickFind.close'),
        closeButton: t('quickFind.close'),
      }}
    />
  );
};

export default QuickFind;
